import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { SERVER_NAME, ETHERSCAN, BSCSCAN } from '../config';
import { useAuth } from '../auth/AuthContext';

const WatchlistContext = createContext();

function normalizeAddress(address) {
  if (!address) return '';
  const raw = String(address).trim();
  if (!raw) return '';
  const base = raw.split('.')[0];
  if (!base.startsWith('0x')) return '';
  return base.toLowerCase();
}

function normalizeChain(chain) {
  const raw = String(chain || '').trim().toUpperCase();
  if (!raw) return 'ETH';
  if (raw === 'ETHEREUM') return 'ETH';
  if (raw === 'BINANCE' || raw === 'BINANCE SMART CHAIN') return 'BSC';
  return raw;
}

function chainFromDataSource(dataSource) {
  if (dataSource === BSCSCAN) return 'BSC';
  if (dataSource === ETHERSCAN) return 'ETH';
  return 'ETH';
}

function makeWatchKey(chain, address) {
  const c = normalizeChain(chain);
  const a = normalizeAddress(address);
  if (!c || !a) return '';
  return `${c}:${a}`;
}

export function WatchlistProvider({ children }) {
  const { isAuthenticated } = useAuth();

  const debug = useMemo(() => String(SERVER_NAME || '').includes('apitest'), []);

  const [items, setItems] = useState([]);
  const [watchedKeys, setWatchedKeys] = useState(() => new Set());
  const [watchlistOnly, setWatchlistOnly] = useState(false);

  const isLoadingRef = useRef(false);

  const rebuildKeys = useCallback((nextItems) => {
    const s = new Set();
    (nextItems || []).forEach((it) => {
      const key = makeWatchKey(it.chain, it.address);
      if (key) s.add(key);
    });
    setWatchedKeys(s);
  }, []);

  const promptLoginForWatchlist = useCallback(() => {
    try {
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('watchlist-login-required', {
          detail: {
            message: 'Login to watch tokens.',
          },
        }));
      }
    } catch (e) {
    }
  }, []);

  const ensureBackendAuthenticated = useCallback(async () => {
    if (isAuthenticated) {
      return true;
    }

    try {
      const resp = await axios.get(`${SERVER_NAME}/auth/status`, { withCredentials: true });
      const ok = !!(resp?.data?.authenticated ?? resp?.data?.isAuthenticated);
      if (ok) return true;
    } catch (e) {
    }

    promptLoginForWatchlist();
    return false;
  }, [isAuthenticated, promptLoginForWatchlist]);

  const loadWatchlist = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setWatchedKeys(new Set());
      return;
    }

    const ok = await ensureBackendAuthenticated();
    if (!ok) {
      setItems([]);
      setWatchedKeys(new Set());
      return;
    }

    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    try {
      const resp = await axios.get(`${SERVER_NAME}/watchlist`, { withCredentials: true });
      const next = resp?.data?.items || [];
      setItems(next);
      rebuildKeys(next);
    } catch (e) {
      // ignore; user might have stale local auth state
    } finally {
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, rebuildKeys]);

  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  const isWatched = useCallback((chain, address) => {
    const key = makeWatchKey(chain, address);
    if (!key) return false;
    return watchedKeys.has(key);
  }, [watchedKeys]);

  const setAlert = useCallback(async ({ chain, address, alertAbove, alertBelow, alertPctChange, alertPctDirection, alertPctWindowHours }) => {
    const ok = await ensureBackendAuthenticated();
    if (!ok) return;
    await axios.patch(`${SERVER_NAME}/watchlist/alert`, {
      chain: String(chain || 'ETH').toUpperCase(),
      address,
      alertAbove: alertAbove != null && alertAbove !== '' ? Number(alertAbove) : null,
      alertBelow: alertBelow != null && alertBelow !== '' ? Number(alertBelow) : null,
      alertPctChange: alertPctChange != null && alertPctChange !== '' ? Number(alertPctChange) : null,
      alertPctDirection: alertPctDirection || null,
      alertPctWindowHours: alertPctWindowHours != null ? Number(alertPctWindowHours) : 24,
    }, { withCredentials: true });
    // Update local items state
    setItems((prev) => prev.map((it) => {
      if (it.chain === String(chain || 'ETH').toUpperCase() && it.address === String(address || '').toLowerCase()) {
        return {
          ...it,
          alertAbove: alertAbove != null && alertAbove !== '' ? Number(alertAbove) : null,
          alertBelow: alertBelow != null && alertBelow !== '' ? Number(alertBelow) : null,
          alertPctChange: alertPctChange != null && alertPctChange !== '' ? Number(alertPctChange) : null,
          alertPctDirection: alertPctDirection || null,
          alertPctWindowHours: alertPctWindowHours != null ? Number(alertPctWindowHours) : 24,
        };
      }
      return it;
    }));
  }, [ensureBackendAuthenticated]);

  const toggleWatch = useCallback(async ({ chain, address, label, type }) => {
    const key = makeWatchKey(chain, address);
    if (!key) return;

    const ok = await ensureBackendAuthenticated();
    if (!ok) {
      return;
    }

    const currentlyWatched = watchedKeys.has(key);

    // optimistic
    if (currentlyWatched) {
      const nextItems = items.filter((it) => makeWatchKey(it.chain, it.address) !== key);
      setItems(nextItems);
      rebuildKeys(nextItems);
    } else {
      const nextItems = [{ chain: normalizeChain(chain), address: normalizeAddress(address), label: label || '', type: type || '', added_at: new Date().toISOString() }, ...items];
      setItems(nextItems);
      rebuildKeys(nextItems);
    }

    try {
      if (currentlyWatched) {
        await axios.delete(`${SERVER_NAME}/watchlist`, {
          withCredentials: true,
          data: { chain: normalizeChain(chain), address: normalizeAddress(address) }
        });
      } else {
        await axios.post(`${SERVER_NAME}/watchlist`, {
          chain: normalizeChain(chain),
          address: normalizeAddress(address),
          label: label || '',
          type: type || ''
        }, { withCredentials: true });
      }
    } catch (e) {
      // revert by reloading from server
      await loadWatchlist();
      throw e;
    }
  }, [items, watchedKeys, rebuildKeys, loadWatchlist, ensureBackendAuthenticated]);

  const value = useMemo(() => ({
    items,
    watchedKeys,
    watchlistOnly,
    setWatchlistOnly,
    isWatched,
    toggleWatch,
    setAlert,
    makeWatchKey,
    normalizeAddress,
    chainFromDataSource,
  }), [items, watchedKeys, watchlistOnly, isWatched, toggleWatch, setAlert]);

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist() {
  const ctx = useContext(WatchlistContext);
  if (!ctx) throw new Error('useWatchlist must be used within WatchlistProvider');
  return ctx;
}
